import { useIntl } from 'react-intl';

import type { AutocompleteSuggestionPart } from '@dynatrace-sdk/client-query';

import { DocumentationLink } from './DocumentationLink.js';
import { CodeSnippet } from '../../../../content/code-snippet/CodeSnippet.js';
import { Flex } from '../../../../layouts/flex/Flex.js';
import { Text } from '../../../../typography/text/Text.js';
import { dqlEditorMessages as messages } from '../messages.js';

type SuggestionPartProps = {
  /**
   * The autocomplete suggestion part supplying the info text and optional synopsis.
   */
  suggestionPart: AutocompleteSuggestionPart;
  /**
   * Optional heading displayed above the info text.
   */
  title?: string;
};

/**
 * Renders the detail body for a single autocomplete suggestion info part.
 */
export const SuggestionPart = ({
  suggestionPart,
  title,
}: SuggestionPartProps) => {
  const intl = useIntl();
  return (
    <Flex flexDirection="column" gap={8}>
      <Flex flexDirection="column" gap={4}>
        {title && <Text textStyle="base-emphasized">{title}</Text>}
        <Text textStyle="base">{suggestionPart.info}</Text>
      </Flex>

      {suggestionPart.synopsis && (
        <Flex flexDirection="column" gap={4}>
          <Text textStyle="small">
            {intl.formatMessage(messages.suggestionSyntaxLabel)}
          </Text>
          <CodeSnippet
            language="dql"
            showLineNumbers={false}
            showCopyAction={false}
            lineBreaks
          >
            {suggestionPart.synopsis}
          </CodeSnippet>
        </Flex>
      )}

      <DocumentationLink suggestionPart={suggestionPart} />
    </Flex>
  );
};
